import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface User {
  email: string;
  name: string;
  url: string;
}

export interface Organization {
  id: string;
  name: string;
}

export interface Membership {
  id: string;
  user_id: string;
  organization_id: string;
  role: "owner" | "admin" | "member";
  is_default: boolean;
}

interface AuthState {
  // Server-side session is the source of truth; this flag is a local
  // hint used by the route guard so we don't redirect to /login while
  // waiting for ``GET /auth/me`` on cold start.
  isAuthenticated: boolean;
  organizationId: string | null;
  locationId: string | null;

  setAuthenticated: (value: boolean) => void;
  setOrganization: (id: string) => void;
  setLocation: (id: string | null) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      organizationId: null,
      locationId: null,

      setAuthenticated: (value) => set({ isAuthenticated: value }),
      setOrganization: (id) => set({ organizationId: id }),
      setLocation: (id) => set({ locationId: id }),

      logout: () =>
        set({
          isAuthenticated: false,
          organizationId: null,
          locationId: null,
        }),
    }),
    {
      name: "auth-storage",
      // localStorage so the picked sede / org is shared across tabs of
      // the same operator. The storage event listener below calls
      // ``persist.rehydrate`` so a change in one tab updates the others
      // without requiring a reload.
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        organizationId: state.organizationId,
        locationId: state.locationId,
      }),
    },
  ),
);

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "auth-storage") {
      void useAuthStore.persist.rehydrate();
    }
  });
}

export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
